/* Copyright (c) 2026, NVIDIA CORPORATION.  All rights reserved.
 *
 * NVIDIA CORPORATION and its licensors retain all intellectual property
 * and proprietary rights in and to this software, related documentation
 * and any modifications thereto.  Any use, reproduction, disclosure or
 * distribution of this software and related documentation without an express
 * license agreement from NVIDIA CORPORATION is strictly prohibited.
 */

import { Injectable } from '@angular/core';
import { Observable, Observer, of } from 'rxjs';
import { filter } from 'rxjs/operators';
import { BridgeCommands } from 'bridge-commands/bridge.commands';
import { generateUuid } from 'utils/lib/common/common-utils';
import {
    ShellMessageType,
    VegaBridgeErrorCode,
    VegaBridgeRequest,
    VegaBridgeResponse
} from 'utils/lib/platform/vega/bridge.types';
import { VegaHelperService } from 'utils/lib/platform/vega/vega-helper.service';
import { LoggingService } from '../logging/logging.service';
import { BridgeMockService } from './bridge.mocks.service';
import { FullBridgeCommand } from './bridge.service';
import { BrowserPluginManager } from './browser-plugin/browser-plugin-manager.service';
import { PluginService } from './plugin.service';
import { VegaPluginManager } from './vega-plugin/vega-plugin-manager.service';

const DEFAULT_REQUEST_TIMEOUT_MS = 30000;

interface PendingRequest {
    observer: Observer<VegaBridgeResponse>;
    timeoutId: ReturnType<typeof setTimeout>;
}

/**
 * Vega bridge command service. Executes bridge commands through Vega-native
 * plugins (falling back to browser plugins) and owns the bridge request/response
 * protocol on top of the VegaHelperService transport: it wraps outbound requests
 * as `BridgeCommand` messages and correlates inbound `BridgeResponse` messages
 * back to their pending requests.
 */
@Injectable()
export class VegaPluginService extends PluginService {
    private static readonly pendingRequestMap = new Map<string, PendingRequest>();

    constructor(
        loggingService: LoggingService,
        vegaPluginManager: VegaPluginManager,
        browserPluginManager: BrowserPluginManager,
        private vegaHelper: VegaHelperService,
        bridgeMocks?: BridgeMockService
    ) {
        super(loggingService.getLogger('crimson/vega'), vegaPluginManager, browserPluginManager, bridgeMocks);
        // Bridge responses are just one message type on the transport.
        this.vegaHelper.shellMessages$
            .pipe(filter(message => message.messageType === ShellMessageType.BridgeResponse))
            .subscribe(message => VegaPluginService.dispatchResponse(message.data as VegaBridgeResponse));
    }

    /**
     * Fire-and-forget bridge command (no response expected). Static so Vega
     * plugins can send without injecting this service, which would create a DI
     * cycle (VegaPluginService → VegaPluginManager → VEGA_PLUGINS → plugin).
     */
    static send(request: VegaBridgeRequest): void {
        VegaHelperService.postToShell({ messageType: ShellMessageType.BridgeCommand, data: request });
    }

    executeCommandInternal<ParamType extends object, ResponseType>(
        command: BridgeCommands.Command<ParamType, ResponseType>,
        params: ParamType,
        shouldLogPayload: boolean,
        shouldIncludePayloadInError: boolean
    ): Observable<ResponseType> {
        const fullBridgeCommand: FullBridgeCommand<ParamType> = this.makeFullBridgeCommand(
            command,
            params,
            command.legacyCommand !== PluginService.EXTENSION_MESSAGE
        );

        if (this.handlerMap.has(fullBridgeCommand.module)) {
            return this.executeCommandUsingPlugin(command, params, shouldLogPayload, shouldIncludePayloadInError);
        }

        this.logger.info(
            'Command not found',
            JSON.stringify(shouldLogPayload ? fullBridgeCommand : fullBridgeCommand.command)
        );
        return of(null);
    }

    /** Send a bridge command and wait for a matching response id from the RN shell. */
    static sendWithResponse(
        request: VegaBridgeRequest,
        timeoutMs = DEFAULT_REQUEST_TIMEOUT_MS
    ): Observable<VegaBridgeResponse> {
        const id = request.id ?? generateUuid(true);
        const requestWithId: VegaBridgeRequest = { ...request, id };

        return new Observable<VegaBridgeResponse>(observer => {
            const timeoutId = setTimeout(() => {
                VegaPluginService.pendingRequestMap.delete(id);
                observer.error({
                    code: VegaBridgeErrorCode.Timeout,
                    message: `Bridge request timed out: ${request.category}:${request.method}`
                });
            }, timeoutMs);

            VegaPluginService.pendingRequestMap.set(id, { observer, timeoutId });
            VegaHelperService.postToShell({ messageType: ShellMessageType.BridgeCommand, data: requestWithId });

            return () => {
                const pending = VegaPluginService.pendingRequestMap.get(id);
                if (pending) {
                    clearTimeout(pending.timeoutId);
                    VegaPluginService.pendingRequestMap.delete(id);
                }
            };
        });
    }

    private static dispatchResponse(response: VegaBridgeResponse): void {
        if (!response?.id) {
            return;
        }
        const pending = VegaPluginService.pendingRequestMap.get(response.id);
        if (!pending) {
            return;
        }
        clearTimeout(pending.timeoutId);
        VegaPluginService.pendingRequestMap.delete(response.id);
        if (response.handled) {
            pending.observer.next(response);
            pending.observer.complete();
        } else {
            pending.observer.error(
                response.error ?? {
                    code: VegaBridgeErrorCode.Unavailable,
                    message: 'Bridge request was not handled'
                }
            );
        }
    }
}
